import { z } from "zod/v4"
import { convertDateTimeLocalToEpochMillis } from "./time"

export const BooleanAllowString = z.preprocess(
  (val) => {
    if (typeof val === "string") {
      if (val.toLowerCase() === "true") return true
      if (val.toLowerCase() === "false") return false
      if (val.toLowerCase() === "null") return undefined
    }
    return Boolean(val)
  },
  z.coerce.boolean()
)

export const nullableDateTransform = z.preprocess(
  (val) => {
    if (val === null || val === undefined || val === 0 || val === "0") {
      return undefined
    }
    if (val instanceof Date) {
      return val.getTime() === 0 ? undefined : val
    }
    if (typeof val === "string") {
      const parsed = new Date(val)
      return isNaN(parsed.getTime()) ? val : parsed
    }
    if (typeof val === "number") {
      const parsed = new Date(val)
      return isNaN(parsed.getTime()) ? val : parsed
    }
    return val
  },
  z.date().optional()
)

export const strictDateTransform = z.preprocess(
  (val) => {
    if (val === null || val === undefined || val === 0 || val === "0") {
      return val
    }
    if (val instanceof Date) {
      return val.getTime() === 0 ? undefined : val
    }
    if (typeof val === "string") {
      const parsed = new Date(val)
      return isNaN(parsed.getTime()) ? val : parsed
    }
    if (typeof val === "number") {
      const parsed = new Date(val)
      return isNaN(parsed.getTime()) ? val : parsed
    }
    return val
  },
  z.date()
)

/**
 * 空文字であれば undefined に変換するテキストボックスに使う想定
 */
export const zOptionalTextInput = z.string().pipe(z.preprocess((val) => (val === "" ? undefined : val), z.string().optional()))
export const zNullableTextInput = z.string().pipe(z.preprocess((val) => (val === "" ? null : val), z.string().nullable()))

/**
 * 空文字を許容しないテキストボックスに使う想定
 */
export const zNonEmptyTextInput = z.string().min(1, "必須要素です")

/**
 * datetime-local を使ったテキストボックスに使う想定
 * IN: string
 * OUT: number (epoch millis) | undefined
 */
export const zOptionalDateTimeTextInput = zOptionalTextInput.transform((val, ctx) => {
  if (val === undefined) return undefined
  const epochMillis = convertDateTimeLocalToEpochMillis(val)
  if (isNaN(epochMillis)) {
    ctx.addIssue({
      code: "custom",
      message: "正しい形式で入力してください",
    })
    return z.NEVER
  }
  return epochMillis
})

/**
 * zOptionalDateTimeTextInput と同じだが、空文字を許容しない
 */
export const zDateTimeTextInput = zOptionalDateTimeTextInput.transform((val, ctx) => {
  if (val === undefined) {
    ctx.addIssue({
      code: "custom",
      message: "設定してください",
    })
    return z.NEVER
  }
  return val
})

// 日時範囲の共通スキーマ定義
export const zDateTimeRangeInput = z.object({
  startAt: zNonEmptyTextInput.transform((val) => convertDateTimeLocalToEpochMillis(val)),
  endAt: zOptionalTextInput.transform((val) => (val == null || val === "") ? null : convertDateTimeLocalToEpochMillis(val)),
  unlimited: z.boolean(),
}).check((ctx) => {
  const { unlimited, endAt, startAt } = ctx.value;
  if (!unlimited && endAt === null) {
    ctx.issues.push({
      input: endAt,
      code: "custom",
      path: ["endAt"],
      message: "終了日時を入力してください",
    })
  }
  if (!unlimited && endAt != null && endAt < startAt) {
    ctx.issues.push({
      path: ["endAt"],
      input: endAt,
      code: "custom",
      message: "終了日時は開始日時より後に設定してください",
    })
  }
})

/**
 * 入力値がstringであろうとnumberであろうとepoch millisに変換するzod
 */
export const zDateTimeInputField = z.preprocess(
  (val) => {
    if (val == null) return undefined
    if (typeof val === "number") return val
    if (typeof val === "string") return convertDateTimeLocalToEpochMillis(val)
    return undefined
  },
  z.number().optional()
)
